import { useState } from 'react'
import { ChevronDown, Globe, Check, Warehouse, Plane, CalendarClock } from 'lucide-react'
import { useLocationAccess } from '@/hooks/useLocationAccess'
import { useAppContext, useLocationNavigation } from '@/hooks/routing'
import { useAllLocationStats, getStatsForLocation } from '@/hooks/useLocationStats'
import { getLocationColor, NETWORK_COLOR } from '@/lib/locationColors'
import { normalizeSlug } from '@/lib/slugs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface LocationContextDropdownProps {
  collapsed: boolean
}

export function LocationContextDropdown({ collapsed }: LocationContextDropdownProps) {
  const [open, setOpen] = useState(false)
  const { accessibleLocations, hasMultipleLocations } = useLocationAccess()
  const { scope, locationSlug, locationName, locationId } = useAppContext()
  const { switchLocation, goToOrgView } = useLocationNavigation()
  const { data: allStats } = useAllLocationStats()

  const isNetwork = scope === 'org'
  const currentLoc = accessibleLocations.find(
    (l) => normalizeSlug(l.location_code) === locationSlug
  )
  const currentColor = currentLoc
    ? getLocationColor(currentLoc.location_code)
    : NETWORK_COLOR
  const currentStats = locationId
    ? getStatsForLocation(allStats, locationId)
    : undefined

  const handleSelectLocation = (loc: typeof accessibleLocations[0]) => {
    setOpen(false)
    switchLocation(normalizeSlug(loc.location_code))
  }

  const handleSelectNetwork = () => {
    setOpen(false)
    goToOrgView({ resource: 'dashboard' })
  }

  // Collapsed: just show an icon with tooltip
  if (collapsed) {
    return (
      <div className="flex justify-center px-1 py-1">
        <Tooltip>
          <Popover open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
                >
                  {isNetwork ? (
                    <Globe className="h-4 w-4" style={{ color: NETWORK_COLOR }} />
                  ) : (
                    <span
                      className="font-mono text-[10px] font-bold"
                      style={{ color: currentColor }}
                    >
                      {currentLoc?.iata_code || currentLoc?.location_code?.slice(0, 3).toUpperCase() || '???'}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <PopoverContent
              side="right"
              align="start"
              className="w-[240px] p-1.5"
              sideOffset={8}
            >
              <DropdownList
                locations={accessibleLocations}
                selectedSlug={locationSlug}
                isNetwork={isNetwork}
                hasMultipleLocations={hasMultipleLocations}
                allStats={allStats}
                onSelectLocation={handleSelectLocation}
                onSelectNetwork={handleSelectNetwork}
              />
            </PopoverContent>
          </Popover>
          <TooltipContent side="right" align="center">
            {isNetwork ? 'All Locations' : locationName || 'Select Location'}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  // Expanded: full dropdown trigger
  return (
    <div className="px-2 pb-1.5 shrink-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all',
              open
                ? 'border-sidebar-border bg-sidebar-accent'
                : 'border-sidebar-border/50 bg-sidebar-accent/30 hover:bg-sidebar-accent/50',
              !hasMultipleLocations && 'cursor-default'
            )}
            disabled={!hasMultipleLocations}
          >
            {/* Location badge */}
            {isNetwork ? (
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                style={{ background: 'linear-gradient(135deg, #132f6e, #1b449d)' }}
              >
                <Globe className="h-3.5 w-3.5 text-blue-200" strokeWidth={1.5} />
              </div>
            ) : (
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border"
                style={{
                  background: `${currentColor}10`,
                  borderColor: `${currentColor}20`,
                }}
              >
                <span
                  className="font-mono text-[10px] font-bold"
                  style={{ color: currentColor }}
                >
                  {currentLoc?.iata_code || currentLoc?.location_code?.slice(0, 3).toUpperCase() || '???'}
                </span>
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div
                className="text-[11px] font-bold text-sidebar-foreground leading-tight truncate"
                title={isNetwork ? 'All Locations' : locationName || 'Select Location'}
              >
                {isNetwork ? 'All Locations' : locationName || 'Select Location'}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {isNetwork ? (
                  <span>{accessibleLocations.length} locations</span>
                ) : currentStats ? (
                  <>
                    <span title="Areas" className="flex items-center gap-1">
                      <Warehouse className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                      <span className="font-medium text-sidebar-foreground">{currentStats.areaCount}</span>
                    </span>
                    <span title="Planned aircraft (now & upcoming)" className="flex items-center gap-1">
                      <Plane className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                      <span className="font-medium text-sidebar-foreground">{currentStats.plannedAircraftCount}</span>
                    </span>
                    <span title="Scheduled events" className="flex items-center gap-1">
                      <CalendarClock className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                      <span className="font-medium text-sidebar-foreground">{currentStats.scheduledEventCount}</span>
                    </span>
                  </>
                ) : (
                  <span>{currentLoc?.iata_code || currentLoc?.airport_code || ''}</span>
                )}
              </div>
            </div>

            {/* Right: chevron */}
            {hasMultipleLocations && (
              <div className="flex items-center shrink-0">
                <ChevronDown
                  className={cn(
                    'h-[11px] w-[11px] text-muted-foreground transition-transform duration-200',
                    open && 'rotate-180'
                  )}
                />
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-1.5"
          sideOffset={4}
        >
          <DropdownList
            locations={accessibleLocations}
            selectedSlug={locationSlug}
            isNetwork={isNetwork}
            hasMultipleLocations={hasMultipleLocations}
            allStats={allStats}
            onSelectLocation={handleSelectLocation}
            onSelectNetwork={handleSelectNetwork}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

/** Shared dropdown list used in both collapsed popover and expanded popover */
function DropdownList({
  locations,
  selectedSlug,
  isNetwork,
  hasMultipleLocations,
  allStats,
  onSelectLocation,
  onSelectNetwork,
}: {
  locations: ReturnType<typeof useLocationAccess>['accessibleLocations']
  selectedSlug: string | null
  isNetwork: boolean
  hasMultipleLocations: boolean
  allStats: ReturnType<typeof useAllLocationStats>['data']
  onSelectLocation: (loc: typeof locations[0]) => void
  onSelectNetwork: () => void
}) {
  return (
    <>
      {/* Network option */}
      {hasMultipleLocations && (
        <>
          <button
            onClick={onSelectNetwork}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent',
              isNetwork && 'bg-accent'
            )}
          >
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
              style={{ background: 'linear-gradient(135deg, #132f6e, #1b449d)' }}
            >
              <Globe className="h-3.5 w-3.5 text-blue-200" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-sidebar-foreground">
                All Locations
              </div>
              <div className="text-xs text-muted-foreground">
                Network overview
              </div>
            </div>
            {isNetwork && <Check className="h-3.5 w-3.5 text-[#5b8dd9] shrink-0" />}
          </button>
          <div className="my-1 h-px bg-border" />
        </>
      )}

      {/* Location items */}
      <div className="max-h-[280px] space-y-0.5 overflow-y-auto">
        {locations.map((loc) => {
          const slug = normalizeSlug(loc.location_code)
          const isSelected = !isNetwork && selectedSlug === slug
          const color = getLocationColor(loc.location_code)
          const stats = getStatsForLocation(allStats, loc.id)
          const code = loc.iata_code || loc.location_code?.slice(0, 3).toUpperCase() || '???'

          return (
            <button
              key={loc.id}
              onClick={() => onSelectLocation(loc)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent',
                isSelected && 'bg-accent'
              )}
            >
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border"
                style={{
                  background: `${color}10`,
                  borderColor: `${color}20`,
                }}
              >
                <span
                  className="font-mono text-[10px] font-bold"
                  style={{ color }}
                >
                  {code}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-sidebar-foreground truncate">
                  {loc.name}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {stats ? (
                    <>
                      <span title="Areas" className="flex items-center gap-1">
                        <Warehouse className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                        {stats.areaCount}
                      </span>
                      <span title="Planned aircraft (now & upcoming)" className="flex items-center gap-1">
                        <Plane className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                        {stats.plannedAircraftCount}
                      </span>
                      <span title="Scheduled events" className="flex items-center gap-1">
                        <CalendarClock className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                        {stats.scheduledEventCount}
                      </span>
                    </>
                  ) : (
                    <span>{loc.iata_code || loc.airport_code || ''}</span>
                  )}
                </div>
              </div>
              {isSelected && (
                <Check className="h-3.5 w-3.5 shrink-0" style={{ color }} />
              )}
            </button>
          )
        })}
      </div>
    </>
  )
}
